/**
 * VisualGest — Log de operações (Fase O.8 — 2026-05-14).
 *
 * Mostra o registo de chamadas HTTP entre TP e VisualGest (tabela
 * o_operation_log), outbound e inbound. Útil para diagnóstico.
 *
 * Backend: POST /api/v2/olifel/operation-log
 */

import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getOlifelOperationLog } from "../../services/OlifelServices";

const DIRECTION_FILTERS = [
    { value: "", label: "Todas as direcções" },
    { value: "outbound", label: "Outbound (TP → VG)" },
    { value: "inbound", label: "Inbound (VG → TP)" },
];

const PAGE_SIZE = 50;

export default function VisualGestOperationLog() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [direction, setDirection] = useState("");
    const [errorsOnly, setErrorsOnly] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = async (targetPage = page) => {
        try {
            setLoading(true);
            const r = await getOlifelOperationLog({
                direction: direction || undefined,
                errors_only: errorsOnly || undefined,
                page: targetPage,
                limit: PAGE_SIZE,
            });
            const data = r.data || {};
            setRows(data.entries || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(
                (err && err.response && err.response.data && err.response.data.message) ||
                "Erro ao carregar log de operações"
            );
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(1);
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [direction, errorsOnly]);

    const handlePage = (e) => {
        const next = e.selected + 1;
        setPage(next);
        load(next);
    };

    const statusClass = (s) => {
        if (s == null) return "";
        if (s >= 200 && s < 300) return "text-success";
        if (s >= 400) return "text-danger";
        return "text-warning";
    };

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid container-custom-height-new">
                    <div className="row mb-2">
                        <div className="col-sm-12 d-flex align-items-center">
                            <button
                                type="button"
                                className="next-button btn btn-secondary"
                                onClick={() => navigate("/visualgest")}
                            >
                                <i className="bi bi-arrow-left"></i> {t("back", { defaultValue: "Voltar" })}
                            </button>
                            <h1 className="page-title ms-auto mb-2">
                                {t("visualgest_operation_log_title", {
                                    defaultValue: "VisualGest — Log de operações",
                                })}
                            </h1>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid container-custom-height-new">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card fuelCard">
                                <div className="card-header">
                                    <div className="card-title m-0 float-left d-flex align-items-center" style={{ gap: 12 }}>
                                        <select
                                            className="form-control input-search"
                                            style={{ width: 220 }}
                                            value={direction}
                                            onChange={(e) => setDirection(e.target.value)}
                                        >
                                            {DIRECTION_FILTERS.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                        <label className="m-0 d-flex align-items-center" style={{ gap: 6 }}>
                                            <input
                                                type="checkbox"
                                                checked={errorsOnly}
                                                onChange={(e) => setErrorsOnly(e.target.checked)}
                                            />
                                            {t("errors_only", { defaultValue: "Só erros" })}
                                        </label>
                                    </div>
                                    <div className="card-title m-0 float-right d-flex">
                                        <button
                                            type="button"
                                            className="btn btn-icon-text mr-2"
                                            onClick={() => load()}
                                            disabled={loading}
                                        >
                                            <i className="bi bi-arrow-repeat"></i>
                                            <span className="ms-2">{t("refresh", { defaultValue: "Actualizar" })}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body fuelCardB">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>{t("direction", { defaultValue: "Direcção" })}</th>
                                                    <th>{t("endpoint", { defaultValue: "Endpoint" })}</th>
                                                    <th>HTTP</th>
                                                    <th>{t("duration", { defaultValue: "Duração" })}</th>
                                                    <th>{t("reference", { defaultValue: "Referência" })}</th>
                                                    <th>{t("error", { defaultValue: "Erro" })}</th>
                                                    <th>{t("date", { defaultValue: "Data" })}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading && (
                                                    <tr><td colSpan={7} className="text-center">A carregar…</td></tr>
                                                )}
                                                {!loading && rows.length === 0 && (
                                                    <tr>
                                                        <td colSpan={7} className="text-center">
                                                            {t("no_records", { defaultValue: "Sem registos" })}
                                                        </td>
                                                    </tr>
                                                )}
                                                {!loading && rows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td>{row.direction}</td>
                                                        <td style={{ wordBreak: "break-all" }}>
                                                            <small>{row.http_method} {row.endpoint}</small>
                                                        </td>
                                                        <td className={statusClass(row.http_status)}>
                                                            {row.http_status != null ? row.http_status : "—"}
                                                            {row.attempts > 1 ? ` (${row.attempts}×)` : ""}
                                                        </td>
                                                        <td>{row.duration_ms != null ? `${row.duration_ms} ms` : "—"}</td>
                                                        <td><small>{row.request_ref || "—"}</small></td>
                                                        <td className="text-danger">
                                                            <small>
                                                                {row.error_code
                                                                    ? `${row.error_code}${row.error_message ? ": " + row.error_message : ""}`
                                                                    : "—"}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {row.created_at
                                                                ? new Date(row.created_at).toLocaleString("pt-PT")
                                                                : "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="fuelCF">
                                    <div className="card-footer card-footer-part clearfix">
                                        <div className="pagination pagination-sm m-0 float-right">
                                            <ReactPaginate
                                                previousLabel={<i className="bi bi-chevron-double-left"></i>}
                                                nextLabel={<i className="bi bi-chevron-double-right"></i>}
                                                breakLabel={"..."}
                                                pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={handlePage}
                                                forcePage={page - 1}
                                                containerClassName={"pagination"}
                                                activeClassName={"active"}
                                                pageClassName={"page-item"}
                                                pageLinkClassName={"page-link"}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
